import { io } from "socket.io-client";
import axios from "axios";

let socket = null;
let listeners = []; // { event, cb } — survives reconnect

/**
 * Fetch a realtime JWT token and connect to the Socket.io relay server.
 * No-op if REACT_APP_REALTIME_URL is not set or already connected.
 */
export async function connectSocket() {
  const url = process.env.REACT_APP_REALTIME_URL;
  console.log("[Socket] URL:", url, "already connected:", socket?.connected);
  if (!url || (socket && socket.connected)) return;

  try {
    const { data } = await axios.get("/api/v1/realtime-token");
    const token = data.token;

    socket = io(url, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });

    // Re-register all listeners on reconnect
    socket.on("connect", () => {
      listeners.forEach(({ event, cb }) => {
        socket.off(event, cb);
        socket.on(event, cb);
      });
    });

    // Token refresh on auth error
    socket.on("connect", () => console.log("[Socket] Connected to", url));
    socket.on("connect_error", async (err) => {
      console.error("[Socket] connect_error:", err.message);
      if (err.message === "Invalid token" || err.message === "Authentication required") {
        try {
          const { data: refreshData } = await axios.get("/api/v1/realtime-token");
          socket.auth = { token: refreshData.token };
          socket.connect();
        } catch (e) {
          // Silent — will retry via reconnection logic
        }
      }
    });

    // Attach existing listeners
    listeners.forEach(({ event, cb }) => {
      socket.on(event, cb);
    });
  } catch (err) {
    console.error("[Socket] connectSocket error:", err);
  }
}

/**
 * Disconnect socket and clear all listeners.
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  listeners = [];
}

/**
 * Register an event listener. Survives reconnects.
 */
export function onSocketEvent(event, cb) {
  listeners.push({ event, cb });
  if (socket) socket.on(event, cb);
}

/**
 * Unregister an event listener.
 */
export function offSocketEvent(event, cb) {
  listeners = listeners.filter((l) => !(l.event === event && l.cb === cb));
  if (socket) socket.off(event, cb);
}

/**
 * Join a ride room to receive ride-specific events.
 */
export function joinRideRoom(rideId) {
  if (socket && socket.connected) {
    socket.emit("join-ride", rideId);
  }
}

/**
 * Leave a ride room.
 */
export function leaveRideRoom(rideId) {
  if (socket && socket.connected) {
    socket.emit("leave-ride", rideId);
  }
}

/**
 * Check if socket is currently connected.
 */
export function isSocketConnected() {
  return socket && socket.connected;
}
